import { Map as MapLibreMap } from '@platform/maplibre';
import { MetadataTypes, ProjectTypes } from '@platform/types';
import * as turf from '@turf/turf';
import { Feature, MultiPolygon, Polygon } from '@turf/turf';
import { GeoJSONSource, LngLatBounds, Map, PointLike, StyleSpecification } from 'maplibre-gl';
import React, { useEffect, useMemo, useRef } from 'react';

interface Props {
  workingLayer: ProjectTypes.SocialExplorerWorkingLayer;
  onSelect: (workingLayerId: string, features: ProjectTypes.WorkingLayerFeatureSelection[]) => void;
  liveGeometry: Feature<Polygon | MultiPolygon> | null;
  dataSourceLayer: MetadataTypes.MetadataSocialExplorerDataSourceLayer;
}

const AnalysisPanelWorkingLayerSelectionMap: React.FC<Props> = ({
  workingLayer,
  dataSourceLayer,
  liveGeometry,
  onSelect,
}) => {
  const mapRef = useRef<Map>();
  const geometryRef = useRef<Feature<Polygon | MultiPolygon> | null>();
  const sourceId = `centroids-source:${workingLayer.id}`;
  const layerId = `centroids-layer:${workingLayer.id}`;

  geometryRef.current = liveGeometry;

  const geoLayerId = `${dataSourceLayer['geo-layer-id']}p`;
  const dataset = dataSourceLayer.datasets.find(
    (ds) =>
      ds['dataset-abbreviation'] ===
      (workingLayer as ProjectTypes.SocialExplorerWorkingLayer).metadata.datasetAbbreviation
  ) as MetadataTypes.MetadataSocialExplorerDataSourceLayerDataset;

  const datasetId = dataset['dataset-id'];
  const rowIdentifier = dataset['primary-key-column'];
  const nameIdentifier = dataset['geo-name-column'];

  const mapStyleToUse: StyleSpecification = useMemo(
    () => ({
      layers: [
        {
          id: layerId,
          source: sourceId,
          layout: {
            visibility: 'visible',
          },
          'source-layer': geoLayerId,
          type: 'circle',
          paint: {
            'circle-opacity': 1,
            'circle-radius': 2,
            'circle-color': 'blue',
          },
        },
        {
          id: 'selection',
          type: 'fill',
          source: 'selection',
          paint: {
            'fill-color': 'red',
            'fill-opacity': 0.6,
          },
        },
      ],
      sources: {
        selection: {
          type: 'geojson',
          data: null,
        },
        [sourceId]: {
          type: 'vector',
          tiles: [
            `${process.env.NX_SOCEX_TILES_SERVER}?x={x}&y={y}&z={z}&layers=${geoLayerId}&columns=${geoLayerId}.${datasetId}.${rowIdentifier},${geoLayerId}.${datasetId}.${nameIdentifier}&projection=EPSG-3857`,
          ],
        },
      },
      version: 8,
    }),
    [dataSourceLayer]
  );

  useEffect(() => {
    if (!liveGeometry) return;

    const bbox = turf.bbox(liveGeometry);

    const bounds = new LngLatBounds(
      [bbox[0], bbox[1]], // Southwest coordinates
      [bbox[2], bbox[3]] // Northeast coordinates
    );
    mapRef.current?.fitBounds(bounds, { padding: 0, animate: false });

    const geoJsonSource = mapRef.current?.getSource('selection') as GeoJSONSource;
    geoJsonSource?.setData(liveGeometry);
  }, [liveGeometry]);

  const handleDoneRendering = (map: Map) => {
    if (!geometryRef.current) return;

    const bbox = turf.bbox(geometryRef.current);
    const geo: [PointLike, PointLike] = [
      map.project({ lat: bbox[1], lng: bbox[0] }),
      map.project({
        lat: bbox[3],
        lng: bbox[2],
      }),
    ];

    const centroidsInBoundingBox = map.queryRenderedFeatures(geo, {
      layers: [layerId],
    });

    const matchingFeatures = centroidsInBoundingBox.filter((x) => {
      // @ts-ignore
      const turfPoint = turf.point(x.geometry.coordinates);
      // @ts-ignore
      return turf.booleanPointInPolygon(turfPoint, geometryRef.current);
    });

    const selection = matchingFeatures.map((f) => ({
      name: f.properties[nameIdentifier],
      id: f.properties[rowIdentifier],
      metadata: { summaryLevelId: dataSourceLayer['summary-level'].id },
    }));

    onSelect(workingLayer.id, selection);
  };

  return (
    <div className="h-[2000px] w-[2000px] bg-white/40">
      <MapLibreMap
        style={mapStyleToUse}
        zoom={1}
        center={[0, 0]}
        onMount={(map) => (mapRef.current = map)}
        onRender={handleDoneRendering}
      />
    </div>
  );
};
export default AnalysisPanelWorkingLayerSelectionMap;
